import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import authService from '../services/authService';
import outlookService from '../services/outlookService';

const UserManagement = () => {
  const auth = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [subLoadingEmail, setSubLoadingEmail] = useState(null);
  const [subError, setSubError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddinHelp, setShowAddinHelp] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    disabled: false,
    permissions: [],
    cognito_groups: []
  });
  const [profileData, setProfileData] = useState({
    password: '',
    full_name: '',
    trader_name: ''
  });
  
  // Check if user has account_admin or admin role
  const isAdmin = auth.user && (auth.hasPermission('account_admin') || auth.user.cognito_groups?.includes('admin'));
  
  // Load user data on mount
  useEffect(() => {
    console.log('UserManagement useEffect triggered, auth.user:', auth.user);
    
    if (!auth.user) {
      console.log('No user found, setting error');
      setError('You must be logged in to access this page.');
      setLoading(false);
      return;
    }
    
    console.log('User found:', auth.user.username, 'permissions:', auth.user.permissions);
    
    // Initialize profile data
    setProfileData({
      password: '',
      full_name: auth.user.full_name || '',
      trader_name: auth.user.trader_name || ''
    });
    
    // Load users list only if admin
    const userIsAdmin = auth.user && auth.hasPermission('account_admin');
    console.log('User is admin:', userIsAdmin);
    
    // Always set loading to false initially to see if the hanging is caused by loadUsers
    setLoading(false);
    
    if (userIsAdmin) {
      console.log('Loading users for admin...');
      // Add a small delay to see if immediate execution is the issue
      setTimeout(() => {
        loadUsers();
      }, 100);
    }

    // Load subscriptions for current user (admins get all; others get own)
    loadSubscriptions();
  }, [auth]); // Remove isAdmin from dependencies

  const loadSubscriptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const result = await outlookService.getSubscriptions(token);
      setSubscriptions(result.subscriptions || []);
    } catch (err) {
      console.error('Failed to load email monitoring subscriptions:', err);
    }
  };

  const getSubscription = (email) => {
    if (!email) return null;
    const lower = email.toLowerCase();
    return subscriptions.find((s) => {
      // Match via user_email (from clientState)
      if (s.user_email?.toLowerCase() === lower) return true;
      // Fallback: parse email from resource "users/{upn}/mailFolders(...)/messages"
      const parts = (s.resource || '').split('/');
      if (parts.length >= 2 && parts[0].toLowerCase() === 'users') {
        return parts[1].toLowerCase() === lower;
      }
      return false;
    });
  };

  const isSubscribed = (email) => !!getSubscription(email);

  const handleToggleSubscription = async (email) => {
    if (!email) return;
    setSubLoadingEmail(email);
    setSubError('');
    try {
      const token = localStorage.getItem('token');
      if (isSubscribed(email)) {
        await outlookService.unsubscribeUser(token, email);
      } else {
        await outlookService.subscribeUser(token, email);
      }
      await loadSubscriptions();
    } catch (err) {
      setSubError(`Email monitoring error: ${err.message}`);
    } finally {
      setSubLoadingEmail(null);
    }
  };
  
  const loadUsers = async () => {
    try {
      console.log('Starting to load users...');
      setAdminError('');
      setAdminLoading(true);
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      const response = await authService.listUsers(token);
      console.log('Users loaded successfully:', response.users?.length || 0);
      setUsers(response.users);
    } catch (err) {
      console.error('Error loading users:', err);
      setAdminError('Failed to load users: ' + err.message);
    } finally {
      setAdminLoading(false);
    }
  };

  // Handle profile update for current user
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        full_name: profileData.full_name,
        trader_name: profileData.trader_name
      };
      
      // Only include password if provided
      if (profileData.password) {
        updateData.password = profileData.password;
      }
      
      // Use updateProfile for current user's own profile
      await authService.updateProfile(token, updateData);
      setSuccess('Profile updated successfully');
      setProfileData({ ...profileData, password: '' });
      
      // Refresh user data in AuthContext so nav bar and profile reflect changes
      if (auth.refreshUser) {
        const updatedUser = await auth.refreshUser();
        if (updatedUser) {
          setProfileData(prev => ({ ...prev, full_name: updatedUser.full_name || '', trader_name: updatedUser.trader_name || '' }));
        }
      }
    } catch (err) {
      setError('Failed to update profile: ' + err.message);
    }
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setAdminError('');
    setAdminSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await authService.createUser(token, formData);
      setAdminSuccess('User created successfully');
      setShowCreateForm(false);
      setFormData({
        username: '',
        password: '',
        email: '',
        full_name: '',
        disabled: false,
        permissions: [],
        cognito_groups: []
      });
      loadUsers();
    } catch (err) {
      setAdminError('Failed to create user: ' + err.message);
    }
  };
  
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setAdminError('');
    setAdminSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const updateData = { ...formData };
      // Remove password if empty (don't update password)
      if (!updateData.password) {
        delete updateData.password;
      }
      
      await authService.updateUser(token, editingUser.username, updateData);
      setAdminSuccess('User updated successfully');
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        email: '',
        full_name: '',
        disabled: false,
        permissions: [],
        cognito_groups: []
      });
      loadUsers();
    } catch (err) {
      setAdminError('Failed to update user: ' + err.message);
    }
  };
  
  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }
    
    setAdminError('');
    setAdminSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await authService.deleteUser(token, username);
      setAdminSuccess('User deleted successfully');
      loadUsers();
    } catch (err) {
      setAdminError('Failed to delete user: ' + err.message);
    }
  };
  
  const handleResetPassword = async (username) => {
    const newPassword = window.prompt(`Enter new password for ${username}:`);
    if (!newPassword) return;
    
    setAdminError('');
    setAdminSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await authService.resetUserPassword(token, username, newPassword);
      setAdminSuccess('Password reset successfully');
    } catch (err) {
      setAdminError('Failed to reset password: ' + err.message);
    }
  };
  
  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      email: user.email || '',
      full_name: user.full_name || '',
      disabled: user.disabled || false,
      permissions: (user.permissions || []).filter(p => p !== 'admin' && p !== 'account_admin'),
      cognito_groups: user.cognito_groups || []
    });
    setShowCreateForm(false);
  };
  
  const cancelEdit = () => {
    setEditingUser(null);
    setShowCreateForm(false);
    setFormData({
      username: '',
      password: '',
      email: '',
      full_name: '',
      disabled: false,
      permissions: [],
      cognito_groups: []
    });
  };
  
  const handlePermissionChange = (permission, checked) => {
    const newPermissions = checked
      ? [...formData.permissions, permission]
      : formData.permissions.filter(p => p !== permission);
    
    setFormData({ ...formData, permissions: newPermissions });
  };

  const handleGroupChange = (group, checked) => {
    const newGroups = checked
      ? [...formData.cognito_groups, group]
      : formData.cognito_groups.filter(g => g !== group);
    
    setFormData({ ...formData, cognito_groups: newGroups });
  };

  // Cognito groups that map to role-level permissions
  const availableGroups = [
    { name: 'admin', description: 'Full system administrator access — grants all permissions' },
    { name: 'account_admin', description: 'User account management — can manage users and permissions' }
  ];
  
  // DynamoDB-managed permissions (admin and account_admin are managed via Cognito groups)
  const availablePermissions = [
  'chat_assistant',
  'rebalancer',
  'cash_reserves_monitor',
  'transaction_history',
  'trade_tracker',
  'trade_tracker_admin',
  'lstm',
  'sharepoint_upload',
  'cc_management',
  'client_helper',
  'chat',
  'vector_inspector',
  'client_admin',
  'activity_dashboard',
  'prompt_manager',
  'prompt_admin',
  'prompt_user',
  'mass_text',
  'client_verification',
  'resources',
  'x_admin',
  'youtube_admin',
  'sec_admin',
  'doc_chat',
  'company_reviews',
  'company_research',
  'cashflow_admin',
  'redactor',
  'auto_trader',
  'ips_scanner',
  'document_data',
  'document_data_edit',
  'expense_reports_admin',
  'reference_values_admin'
  ];

  const permissionDescriptions = {
    'chat_assistant': 'Legacy chat permissions',
    'rebalancer': 'Access to portfolio rebalancing tools',
    'cash_reserves_monitor': 'Access to Cash Reserves Monitor',
    'transaction_history': 'Access to Transaction History',
    'trade_tracker': 'Access to Trade Tracker features',
    'trade_tracker_admin': 'Can view and select other users in Trade Tracker',
    'lstm': 'Access to the LSTM Trading Lab tool',
    'sharepoint_upload': 'Access to SharePoint upload functionality',
    'cc_management': 'Access to covered calls management',
    'client_helper': 'Access to client helper AI assistant',
    'chat': 'Access to chat/conversation features',
    'vector_inspector': 'Access to vector database inspection tools',
    'client_admin': 'Access to client administration features',
    'activity_dashboard': 'Access to activity tracking and monitoring dashboard',
    'prompt_manager': 'Access to prompt management features',
    'prompt_admin': 'Administrative permissions for prompt management, to manage app-owned prompts',
    'prompt_user': 'Use app-owned prompts in other apps (cannot create or edit them)',
    'mass_text': 'Access to mass text messaging',
    'client_verification': 'Can text one-time codes to voice-verify clients on a call',
    'resources': 'Access to resources AI assistant',
    'x_admin': 'Access to X (Twitter) search management features',
    'youtube_admin': 'Access to YouTube subscription management features',
    'sec_admin': 'Access to SEC filings management features',
    'doc_chat': 'Access to document chat features',
    'company_reviews': 'Access to company reviews (TrustPilot) dashboard',
    'company_research': 'Access to company research, earnings extraction, and custom metrics',
    'cashflow_admin': 'Access to cashflow wizard client management',
    'redactor': 'Access to The Redactor PII redaction tool',
    'auto_trader': 'Access to the AutoTrader desktop tool download page',
    'ips_scanner': 'Access to the IPS (Investment Policy Statement) Scanner tool',
    'document_data': 'Access to the eMoney Build page: client eMoney reports, extracted document data points and the Ask box',
    'document_data_edit': 'On the eMoney Build page, correct and fill in values, check values off as entered in eMoney, and add notes (also needs Document Data)',
    'expense_reports_admin': 'Review, approve or reject expense reports from all employees, add notes and mark them paid (everyone can submit their own)',
    'reference_values_admin': 'Review and approve the annual IRS/SSA limits (401(k), IRA, HSA, Social Security) that eMoney Build and Client Helper use'
  };
  
  if (!auth.user) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          You must be logged in to access this page.
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }
  
  const MANIFEST_URL = 'https://0sd2lz6tz9.execute-api.us-east-1.amazonaws.com/outlook/addin/manifest-prod.xml';
  const OWA_ADDINS_URL = 'https://outlook.office365.com/owa/?path=/options/manageapps';

  const handleDownloadManifest = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(MANIFEST_URL);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'manifest-prod.xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download manifest:', error);
      alert('Failed to download manifest file. Please try again.');
    }
  };

  return (
    <div className="container mt-4">
      {/* My Profile Section - Available to all users */}
      <div className="card mb-4">
        <div className="card-header">
          <h5>My Profile</h5>
        </div>
        <div className="card-body">
          {error && (
            <div className="alert alert-danger alert-dismissible fade show">
              {error}
              <button type="button" className="btn-close" onClick={() => setError('')}></button>
            </div>
          )}
          
          {success && (
            <div className="alert alert-success alert-dismissible fade show">
              {success}
              <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
            </div>
          )}
          
          <form onSubmit={handleProfileUpdate}>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">User ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={auth.user.username}
                    disabled
                  />
                  <div className="form-text">User ID cannot be changed</div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                    autoComplete="off"
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Trader Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={profileData.trader_name}
                    onChange={(e) => setProfileData({ ...profileData, trader_name: e.target.value })}
                    autoComplete="off"
                    placeholder="Name used in order form filenames"
                  />
                  <div className="form-text">Used as your identity in generated order form filenames (e.g. Fidelity and Schwab files)</div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">New Password (leave empty to keep current)</label>
                  <input
                    type="password"
                    className="form-control"
                    value={profileData.password}
                    onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                </div>
                
                <button type="submit" className="btn btn-primary">
                  Update Profile
                </button>
              </div>
              
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Roles &amp; Permissions</label>
                  <div className="border p-3 rounded bg-light">
                    {/* Cognito Groups */}
                    {auth.user.cognito_groups?.filter(g => g === 'admin' || g === 'account_admin').length > 0 && (
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Roles (Cognito Groups):</small>
                        {auth.user.cognito_groups.filter(g => g === 'admin' || g === 'account_admin').map(group => (
                          <div key={group} className="mb-1">
                            <span className="badge bg-warning text-dark me-2">
                              ⭐ {group}
                            </span>
                            {group === 'admin' && (
                              <span className="text-muted small">Full system administrator access</span>
                            )}
                            {group === 'account_admin' && (
                              <span className="text-muted small">User account management</span>
                            )}
                          </div>
                        ))}
                        <hr className="my-2" />
                      </div>
                    )}
                    {/* Feature Permissions - show actual assigned permissions, not admin-inflated */}
                    {(auth.user.assigned_permissions || auth.user.permissions)?.filter(p => p !== 'admin' && p !== 'account_admin').length > 0 ? (
                      <>
                        <small className="text-muted d-block mb-1">Permissions:</small>
                        {(auth.user.assigned_permissions || auth.user.permissions).filter(p => p !== 'admin' && p !== 'account_admin').map(permission => (
                          <div key={permission} className="mb-1">
                            <span className="badge bg-info me-2">
                              {permission}
                            </span>
                            {permissionDescriptions[permission] && (
                              <span className="text-muted small">
                                {permissionDescriptions[permission]}
                              </span>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      auth.user.cognito_groups?.includes('admin') ? (
                        <span className="text-muted">Full access granted via Admin role</span>
                      ) : !auth.user.cognito_groups?.filter(g => g === 'admin' || g === 'account_admin').length && (
                        <span className="text-muted">No permissions assigned</span>
                      )
                    )}
                  </div>
                  <div className="form-text">Permissions are managed by administrators</div>
                </div>

                {/* Email Monitoring Toggle */}
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="form-label mb-0">Email Monitoring</label>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowAddinHelp(true)}
                      title="How to install the Outlook Email Classifier add-in"
                    >
                      <i className="bi bi-envelope-check me-1"></i>Add-in Setup
                    </button>
                  </div>
                  <div className="border p-3 rounded bg-light">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong>Inbox Auto-Processing</strong>
                        <div className="text-muted small">
                          Automatically summarise and classify new emails as they arrive
                        </div>
                      </div>
                      {auth.user.email ? (
                        <div className="form-check form-switch mb-0">
                          {subLoadingEmail === auth.user.email ? (
                            <span className="spinner-border spinner-border-sm text-secondary" role="status" />
                          ) : (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="emailMonitorSelf"
                              checked={isSubscribed(auth.user.email)}
                              onChange={() => handleToggleSubscription(auth.user.email)}
                            />
                          )}
                        </div>
                      ) : (
                        <span className="text-muted small">No email address on account</span>
                      )}
                    </div>
                  </div>
                  {subError && (
                    <div className="text-danger small mt-1">{subError}</div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Admin Section - Only visible to users with account_admin permission */}
      {isAdmin && (
        <>
          <hr className="my-4" />
          
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>User Management <span className="badge bg-warning">Admin</span></h2>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowCreateForm(true);
                setEditingUser(null);
              }}
            >
              Create New User
            </button>
          </div>
          
          {adminError && (
            <div className="alert alert-danger alert-dismissible fade show">
              {adminError}
              <button type="button" className="btn-close" onClick={() => setAdminError('')}></button>
            </div>
          )}
          
          {adminSuccess && (
            <div className="alert alert-success alert-dismissible fade show">
              {adminSuccess}
              <button type="button" className="btn-close" onClick={() => setAdminSuccess('')}></button>
            </div>
          )}
          
          {(showCreateForm || editingUser) && (
            <div className="card mb-4">
              <div className="card-header">
                <h5>{editingUser ? 'Edit User' : 'Create New User'}</h5>
              </div>
              <div className="card-body">
                <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">User ID</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.username}
                          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                          required
                          disabled={editingUser !== null}
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">
                          Password {editingUser && '(leave empty to keep current)'}
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required={!editingUser}
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          disabled={editingUser !== null}
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">Full Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        />
                      </div>
                      
                      <div className="mb-3 form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="disabled"
                          checked={formData.disabled}
                          onChange={(e) => setFormData({ ...formData, disabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="disabled">
                          Account Disabled
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      {/* Cognito Groups (role-level access) */}
                      <label className="form-label">Roles <span className="badge bg-warning text-dark">Cognito Groups</span></label>
                      <div className="border border-warning p-3 rounded mb-3 bg-light">
                        {availableGroups.map(group => (
                          <div key={group.name} className="form-check mb-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`group-${group.name}`}
                              checked={formData.cognito_groups.includes(group.name)}
                              onChange={(e) => handleGroupChange(group.name, e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`group-${group.name}`}>
                              <strong>{group.name}</strong>
                              <div className="text-muted small">
                                {group.description}
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>

                      {/* DynamoDB Permissions (feature-level access) */}
                      <label className="form-label">Permissions</label>
                      <div className="border p-3 rounded" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {availablePermissions.map(permission => (
                          <div key={permission} className="form-check mb-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={permission}
                              checked={formData.permissions.includes(permission)}
                              onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={permission}>
                              <strong>{permission}</strong>
                              {permissionDescriptions[permission] && (
                                <div className="text-muted small">
                                  {permissionDescriptions[permission]}
                                </div>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <button type="submit" className="btn btn-success me-2">
                      {editingUser ? 'Update User' : 'Create User'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          
          <div className="card">
            <div className="card-header">
              <h5>All Users ({users.length})</h5>
            </div>
            <div className="card-body">
              {adminLoading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : users.length === 0 ? (
                <p className="text-muted">No users found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>User ID</th>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Roles / Permissions</th>
                        <th>Status</th>
                        <th className="text-center">Email Monitor</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.username}>
                          <td>
                            <strong>{user.username}</strong>
                            {user.username === auth.user?.username && (
                              <span className="badge bg-primary ms-2">You</span>
                            )}
                          </td>
                          <td>{user.full_name || '-'}</td>
                          <td>{user.email || '-'}</td>
                          <td>
                            {/* Cognito Groups */}
                            {user.cognito_groups?.filter(g => g === 'admin' || g === 'account_admin').length > 0 && (
                              <div className="mb-1">
                                {user.cognito_groups.filter(g => g === 'admin' || g === 'account_admin').map(group => (
                                  <span 
                                    key={group} 
                                    className="badge bg-warning text-dark me-1 mb-1"
                                    title={`Cognito Group: ${group}`}
                                  >
                                    ⭐ {group}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* DynamoDB Permissions */}
                            {user.permissions?.filter(p => p !== 'admin' && p !== 'account_admin').length > 0 ? (
                              <div>
                                {user.permissions.filter(p => p !== 'admin' && p !== 'account_admin').map(permission => (
                                  <span 
                                    key={permission} 
                                    className="badge bg-info me-1 mb-1"
                                    title={permissionDescriptions[permission] || permission}
                                  >
                                    {permission}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              !user.cognito_groups?.filter(g => g === 'admin' || g === 'account_admin').length && (
                                <span className="text-muted">No permissions</span>
                              )
                            )}
                          </td>
                          <td>
                            <span className={`badge ${user.disabled ? 'bg-danger' : 'bg-success'}`}>
                              {user.disabled ? 'Disabled' : 'Active'}
                            </span>
                          </td>
                          <td className="text-center">
                            {user.email ? (
                              subLoadingEmail === user.email ? (
                                <span className="spinner-border spinner-border-sm text-secondary" role="status" />
                              ) : (
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={isSubscribed(user.email)}
                                  onChange={() => handleToggleSubscription(user.email)}
                                  title={isSubscribed(user.email) ? 'Click to disable email monitoring' : 'Click to enable email monitoring'}
                                />
                              )
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => startEdit(user)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-outline-warning"
                                onClick={() => handleResetPassword(user.username)}
                              >
                                Reset Password
                              </button>
                              {user.username !== auth.user?.username && (
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => handleDeleteUser(user.username)}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Email Add-in Setup Help Modal */}
      {showAddinHelp && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={e => { if (e.target === e.currentTarget) setShowAddinHelp(false); }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-envelope-check me-2 text-primary"></i>
                  Installing the HFF Email Classifier Add-in
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowAddinHelp(false)}></button>
              </div>
              <div className="modal-body">

                {/* Download */}
                <div className="alert alert-primary d-flex align-items-center gap-3 mb-4">
                  <i className="bi bi-download fs-4"></i>
                  <div>
                    <strong>Step 1 — Download the manifest file</strong>
                    <div className="mt-1">
                      <button onClick={handleDownloadManifest} className="btn btn-sm btn-primary">
                        Download manifest-prod.xml
                      </button>
                    </div>
                    <div className="text-muted small mt-1">
                      Save the file somewhere easy to find (e.g. Downloads folder).
                    </div>
                  </div>
                </div>

                {/* Install steps */}
                <h6 className="fw-semibold mb-3">Step 2 — Sideload in Outlook on the Web</h6>
                <ol className="mb-4">
                  <li className="mb-2">
                    Open{' '}
                    <a href={OWA_ADDINS_URL} target="_blank" rel="noopener noreferrer">
                      Outlook Web Add-ins Settings
                    </a>{' '}
                    <span className="text-muted small">(outlook.office365.com → Settings → Manage add-ins)</span>
                  </li>
                  <li className="mb-2">Click the <strong>+</strong> icon (Add from file).</li>
                  <li className="mb-2">Select the <code>manifest-prod.xml</code> file you just downloaded.</li>
                  <li className="mb-2">Click <strong>Install</strong> on the confirmation prompt.</li>
                  <li className="mb-2">The <strong>HFF Email Classifier</strong> ribbon button will now appear when you open any email.</li>
                  <li>Click the <strong>pin icon</strong> (📌) in the top-right corner of the task pane to keep it open as you switch between emails.</li>
                </ol>

                {/* What it does */}
                <h6 className="fw-semibold mb-2">What the add-in does</h6>
                <ul className="mb-4">
                  <li>Adds an <strong>"Analyze Email"</strong> button to the Outlook ribbon.</li>
                  <li>Summarises the selected email using AWS Bedrock and suggests a destination folder.</li>
                  <li>Lets you move the email to the suggested (or any) folder with one click.</li>
                  <li>If <strong>Email Monitoring</strong> is enabled on your profile, new emails are Analyzed automatically as they arrive — the summary is pre-loaded when you open the email.</li>
                </ul>

                {/* Requirements */}
                <div className="alert alert-light border mb-0">
                  <h6 className="fw-semibold mb-2"><i className="bi bi-info-circle me-1"></i>Requirements</h6>
                  <ul className="mb-0 small">
                    <li>Microsoft 365 / Exchange Online mailbox (personal Outlook.com accounts are <strong>not</strong> supported).</li>
                    <li>Mailbox must be <code>@hffinancial.com</code> — the backend only processes HFF accounts.</li>
                    <li>The add-in requires <strong>ReadWriteMailbox</strong> permission to move emails; you will be prompted to approve this on first use.</li>
                    <li>If sideloading is blocked, contact your Microsoft 365 admin to enable "User Sideloading of Add-ins" in the Exchange admin centre.</li>
                  </ul>
                </div>

              </div>
              <div className="modal-footer">
                <button onClick={handleDownloadManifest} className="btn btn-primary me-auto">
                  <i className="bi bi-download me-1"></i>Download Manifest
                </button>
                <a href={OWA_ADDINS_URL} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary">
                  Open Outlook Add-ins Settings
                </a>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddinHelp(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
